import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./HomePage.css";
import FilterBar, { mainCategories, subCategoryMappings, preferenceMappings } from "./FilterBar";
import products from "../hair_products.json";
import foodProducts from "../food_products.json";
import { getProductImage, getScoreStyle } from "../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faSearch, faCamera } from "@fortawesome/free-solid-svg-icons";

const categories = [
    { name: "Cilt Bakımı", icon: "🧼", bg: "linear-gradient(135deg, #A2D5AB, #3C8D2F)" },
    { name: "Saç Bakımı", icon: "💇‍♀️", bg: "linear-gradient(135deg, #FFB6C1, #D81B60)" },
    { name: "Vücut Bakımı", icon: "🧴", bg: "linear-gradient(135deg, #FFD54F, #FF6F00)" },
    { name: "Makyaj", icon: "💄", bg: "linear-gradient(135deg, #E91E63, #880E4F)" },
    { name: "Güneş Koruyucu", icon: "☀️", bg: "linear-gradient(135deg, #FFEB3B, #FFC107)" },
    { name: "Parfüm", icon: "🌸", bg: "linear-gradient(135deg, #CE93D8, #6A1B9A)" },
    { name: "Takviye Gıda/Vitamin", icon: "💊", bg: "linear-gradient(135deg, #81C784, #2E7D32)" },
    { name: "İlaç", icon: "🩺", bg: "linear-gradient(135deg, #64B5F6, #1E88E5)" },
    { name: "Yiyecek/İçecek", icon: "🍎", bg: "linear-gradient(135deg, #FF8A80, #D32F2F)" },
];

const HomePage = () => {
    const navigate = useNavigate();
    const [recentScannedProducts, setRecentScannedProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isIosDevice, setIsIosDevice] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState({
        mainCategory: "",
        subCategory: [],
        preferences: [],
        ingredients: [],
        goodFor: [],
    });
    const [isOpen, setIsOpen] = useState(false);

    const currentProductsData = useMemo(() => {
        return (selectedFilters.mainCategory === "Takviye Gıda/Vitamin" || selectedFilters.mainCategory === "Yiyecek/İçecek")
            ? foodProducts
            : products;
    }, [selectedFilters.mainCategory]);

    const allProducts = useMemo(
        () => Object.values(currentProductsData).flat(),
        [currentProductsData]
    );
    const handleNavigation = (path) => {
        navigate(path);
    };

    useEffect(() => {
        const userAgent = window.navigator.userAgent.toLowerCase();
        setIsIosDevice(/iphone|ipad|ipod/.test(userAgent));
        const handleAppInstalled = () => setIsInstalled(true);
        window.addEventListener("appinstalled", handleAppInstalled);
        setIsInstalled(window.matchMedia("(display-mode: standalone)").matches);
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        return () => {
            window.removeEventListener("appinstalled", handleAppInstalled);
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            await deferredPrompt.userChoice;
            setDeferredPrompt(null);
        }
    };

    useEffect(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            setFilteredProducts([]);
        } else {
            setFilteredProducts(
                allProducts.filter(
                    (product) =>
                        product.name.toLowerCase().includes(query) ||
                        product.brand.toLowerCase().includes(query)
                )
            );
        }
    }, [searchQuery, allProducts]);

    useEffect(() => {
        const scannedBarcodes = JSON.parse(localStorage.getItem("scannedBarcodes")) || [];
        let recentProducts;
        if (scannedBarcodes.length > 0) {
            recentProducts = scannedBarcodes
                .map((barcode) => {
                    const category = Object.keys(products).find((key) =>
                        products[key].some((p) => p.barcode === barcode)
                    );
                    const product = category ? products[category].find(p => p.barcode === barcode) : null;
                    return product ? { ...product, category } : null;
                })
                .filter(Boolean);
        } else {
            const allProductsList = Object.entries(products)
                .flatMap(([category, items]) => items.map((item) => ({ ...item, category })));
            recentProducts = allProductsList
                .sort(() => 0.5 - Math.random())
                .slice(0, 10);
        }
        setRecentScannedProducts(recentProducts);
    }, []);

    const filteredProductsList = useMemo(() => {
        const { mainCategory, subCategory, preferences, ingredients, goodFor } = selectedFilters;
        if (!mainCategory && subCategory.length === 0 && preferences.length === 0 && ingredients.length === 0 && goodFor.length === 0)
            return [];
        const categoryTranslation = {
            "Shampoo": "Şampuan",
            "Hair Condititioner": "Saç Kremi",
            "Hair Mask": "Saç Maskesi",
            "Hair Serum": "Saç Serumu",
            "Hair Oil": "Saç Yağı",
            "Hair Care": "Saç Bakımı",
            "Men Hair Care": "Saç Bakımı",
            "Hair Styling": "Saç Şekillendirme",
            "Hair Color": "Saç Boyası",
            "Hair Color Opener": "Saç Boyası",
            "Makyaj": "Makyaj",
            "Takviye Gıda/Vitamin": "Takviye Gıda/Vitamin",
            "Yiyecek/İçecek": "Yiyecek/İçecek"
        };
        return allProducts.filter((product) => {
            // Use currentProductsData here instead of products
            let productCategory = Object.keys(currentProductsData).find((key) =>
                currentProductsData[key].some((p) => p.barcode === product.barcode)
            );
            if (!productCategory) return false;
            if (categoryTranslation[productCategory]) productCategory = categoryTranslation[productCategory];
            if (mainCategory) {
                const possibleSubCats = Object.keys(subCategoryMappings[mainCategory] || {});
                if (subCategory.length > 0) {
                    if (!subCategory.includes(productCategory)) return false;
                } else {
                    if (!possibleSubCats.includes(productCategory)) return false;
                }
            }
            if (preferences.length > 0) {
                for (let pref of preferences) {
                    const flagKey = preferenceMappings[pref];
                    if (flagKey && product.flags[flagKey] && product.flags[flagKey].length > 0) {
                        return false;
                    }
                }
            }
            if (ingredients.length > 0) {
                if (!ingredients.every(ing => product.ingredients.includes(ing))) return false;
            }
            if (goodFor.length > 0) {
                if (mainCategory === "Saç Bakımı") {
                    if (!goodFor.every(gf =>
                        Object.values(product.hair_scalp_analysis.hair).some(h =>
                            h.found_good.includes(gf) || h.overall === "Recommended"
                        ) ||
                        Object.values(product.hair_scalp_analysis.scalp).some(s =>
                            s.found_good.includes(gf) || s.overall === "Recommended"
                        )
                    )) return false;
                } else {
                    if (!product.analysis || !goodFor.every(gf => product.analysis.found_good?.includes(gf))) return false;
                }
            }
            return true;
        });
    }, [selectedFilters, allProducts]);

    const removeFilter = (type, value) => {
        setSelectedFilters((prev) => ({
            ...prev,
            [type]: type === "mainCategory" ? "" : prev[type].filter((item) => item !== value),
        }));
    };

    const toggleMainCategoryFilter = (categoryName) => {
        setSelectedFilters((prev) => {
            if (prev.mainCategory === categoryName) {
                return { ...prev, mainCategory: "", subCategory: [] };
            } else {
                return { ...prev, mainCategory: categoryName, subCategory: [] };
            }
        });
    };

    return (
        <div className="home-page">
            <div className="search-container">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                    type="text"
                    className="search-bar"
                    placeholder="Ürün ara veya barkod tara"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="filter-button" onClick={() => setIsOpen(true)} aria-label="Filtreleri Aç">
                    <FontAwesomeIcon icon={faFilter} />
                </button>
                <button className="camera-button" onClick={() => handleNavigation("/scan")} aria-label="Scan barcode">
                    <FontAwesomeIcon icon={faCamera} />
                </button>
            </div>

            <div className="support-button-section" style={{ textAlign: "right", margin: "1rem" }}>
                <button
                    className="support-button"
                    onClick={() => handleNavigation("/support")}
                    style={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "0.9rem",
                        border: "none",
                        cursor: "pointer",
                    }}
                >
                    ❓ Yardım / Destek
                </button>
            </div>

            {searchQuery && filteredProducts.length > 0 && (
                <div className="dropdown-container">
                    {filteredProducts.map((product, index) => {
                        const category = Object.keys(products).find((key) =>
                            products[key].some((p) => p.barcode === product.barcode)
                        );
                        return (
                            <div
                                key={`${product.barcode || product.id}-${index}`}
                                className="dropdown-item"
                                onClick={() => handleNavigation(`/product/${product.barcode || `id-${product.id}`}`)}
                            >
                                <img
                                    src={product.barcode ? getProductImage(category, product.barcode, product.id) : product.image}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = product.image;
                                    }}
                                    alt={product.name}
                                    className="dropdown-img"
                                />
                                <div>
                                    <span className="dropdown-name">{product.name}</span>
                                    <div className="dropdown-brand">{product.brand}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {Object.entries(selectedFilters).some(
                ([key, value]) => (Array.isArray(value) ? value.length > 0 : value)
            ) && (
                    <div className="filter-section">
                        <div className="filter-labels">
                            {Object.entries(selectedFilters).flatMap(([type, values]) =>
                                Array.isArray(values)
                                    ? values.map((value) => (
                                        <span key={`${type}-${value}`} className="filter-tag">
                                            {value} <button onClick={() => removeFilter(type, value)}>✖</button>
                                        </span>
                                    ))
                                    : values && (
                                        <span key={`${type}-${values}`} className="filter-tag">
                                            {values} <button onClick={() => removeFilter(type, values)}>✖</button>
                                        </span>
                                    )
                            )}
                        </div>
                        <div className="filter-toggle-container">
                            <button className="filter-toggle-btn" onClick={() => setIsOpen(true)}>
                                Filtreleri Göster
                            </button>
                        </div>
                    </div>
                )}

            {!Object.entries(selectedFilters).some(
                ([key, value]) => (Array.isArray(value) ? value.length > 0 : value)
            ) && (
                    <>
                        <div className="modern-category-grid">
                            {categories.map((category) => (
                                <button
                                    key={category.name}
                                    className={`modern-category-card ${selectedFilters.mainCategory === category.name ? "active" : ""}`}
                                    onClick={() => toggleMainCategoryFilter(category.name)}
                                    style={{ background: category.bg }}
                                >
                                    <span className="modern-category-icon">{category.icon}</span>
                                    <span className="modern-category-name">{category.name}</span>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            <FilterBar
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
            />

            {filteredProductsList.length > 0 && (
                <div className="filtered-products">
                    <h3>Filtrelenmiş Ürünler</h3>
                    <div className="filtered-products-container">
                        {filteredProductsList.map((product, index) => {
                            const category = Object.keys(products).find((key) =>
                                products[key].some((p) => p.barcode === product.barcode)
                            );
                            return (
                                <div
                                    key={`${product.barcode}-${index}`}
                                    className="filtered-product-card"
                                    onClick={() => handleNavigation(`/product/${product.barcode || `id-${product.id}`}`)}
                                >
                                    <div className="image-container">
                                        <img
                                            src={getProductImage(category, product.barcode, product.id)}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = product.image;
                                            }}
                                            alt={product.name}
                                            className="filtered-product-img"
                                        />
                                        <div className="score-circle" style={getScoreStyle(product.total_score)}>
                                            <span className="score-big">{product.total_score}</span>
                                            <span className="score-small">/100</span>
                                        </div>
                                    </div>
                                    <div className="filtered-product-info">
                                        <h4 className="filtered-product-name">{product.name}</h4>
                                        <p className="filtered-product-brand">{product.brand}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="recent-products">
                <h3>
                    {(JSON.parse(localStorage.getItem("scannedBarcodes")) || []).length > 0
                        ? "Son Taranan Ürünler"
                        : "Keşfetmeye Başla"}
                </h3>
                <div className="scanned-products">
                    {recentScannedProducts.length > 0 &&
                        recentScannedProducts.map((product, index) => (
                            <div
                                key={`${product.barcode}-${index}`}
                                className="filtered-product-card"
                                onClick={() => handleNavigation(`/product/${product.barcode}`)}
                            >
                                <div className="image-container">
                                    <img
                                        src={getProductImage(product.category, product.barcode, product.id)}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = product.image;
                                        }}
                                        alt={product.name}
                                        className="filtered-product-img"
                                    />
                                    <div className="score-circle" style={getScoreStyle(product.total_score)}>
                                        <span className="score-big">{product.total_score}</span>
                                        <span className="score-small">/100</span>
                                    </div>
                                </div>
                                <div className="filtered-product-info">
                                    <h4 className="filtered-product-name">{product.name}</h4>
                                    <p className="filtered-product-brand">{product.brand}</p>
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            {!searchQuery &&
                filteredProducts.length === 0 &&
                !isInstalled &&
                !Object.entries(selectedFilters).some(
                    ([, value]) => (Array.isArray(value) ? value.length > 0 : value)
                ) && (
                    <>
                        {isIosDevice && (
                            <div className="app-install-section">
                                <p>
                                    📱 Uygulamayı yüklemek için: Tarayıcınızdaki paylaş simgesine dokunun ve "Ana Ekrana Ekle" seçeneğini seçin.
                                </p>
                            </div>
                        )}

                        {!isIosDevice && deferredPrompt && (
                            <div className="app-install-section">
                                <button className="install-button" onClick={handleInstallClick}>
                                    📲 Uygulamayı Telefona Yükle
                                </button>
                            </div>
                        )}
                    </>
                )}

        </div>
    );
};

export default HomePage;
